import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { formatDateTime } from "@/lib/utils";
import {
  Flag,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Search,
  Building2,
  History,
  Layers,
  Settings,
  Check,
  X,
} from "lucide-react";

const FLAG_DESCRIPTIONS: Record<string, string> = {
  ai_assistant: "Assistant IA — suggestions et auto-complétion",
  sms_reminders: "Relances SMS automatiques clients",
  custom_smtp: "Configuration SMTP personnalisée par tenant",
  mobile_app: "Accès à l'application mobile",
  outgoing_webhooks: "Webhooks sortants vers systèmes tiers",
  recurring_invoices: "Factures récurrentes et abonnements",
  client_portal: "Portail client — accès extranet",
};

const ALL_FEATURES = [
  "quotes", "invoices", "purchase_invoices", "purchase_orders",
  "interventions", "planning", "timesheets", "reports",
  "documents", "messaging", "reminders", "ai_assistant",
  "client_portal", "recurring_invoices", "custom_smtp", "outgoing_webhooks",
  "certifications", "expenses", "materials", "service_catalog",
  "subcontractors", "retenues", "situations", "checklists",
];

const FEATURE_LABELS: Record<string, string> = {
  quotes: "Devis",
  invoices: "Factures",
  purchase_invoices: "Factures fournisseurs",
  purchase_orders: "Bons de commande",
  interventions: "Interventions",
  planning: "Planning",
  timesheets: "Feuilles de temps",
  reports: "Rapports",
  documents: "Documents",
  messaging: "Messagerie",
  reminders: "Relances",
  ai_assistant: "Assistant IA",
  client_portal: "Portail client",
  recurring_invoices: "Factures récurrentes",
  custom_smtp: "SMTP personnalisé",
  outgoing_webhooks: "Webhooks sortants",
  certifications: "Certifications",
  expenses: "Notes de frais",
  materials: "Matériaux",
  service_catalog: "Catalogue services",
  subcontractors: "Sous-traitants",
  retenues: "Retenues de garantie",
  situations: "Situations de travaux",
  checklists: "Checklists",
};

const PLAN_DEFAULTS: Record<string, string[]> = {
  free: ["quotes", "invoices", "documents", "messaging"],
  artisan: ["quotes", "invoices", "purchase_invoices", "interventions", "planning", "timesheets", "documents", "messaging", "reminders", "certifications"],
  enterprise: ALL_FEATURES.filter(f => f !== "purchase_orders" && f !== "outgoing_webhooks"),
  pro_btp: [...ALL_FEATURES],
};

const PLAN_LABELS: Record<string, string> = {
  free: "Gratuit",
  artisan: "Artisan",
  enterprise: "Enterprise",
  pro_btp: "Pro BTP",
};

type Tab = "global" | "plans" | "company";

export default function FeatureFlags() {
  const [tab, setTab] = useState<Tab>("global");

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Flag className="h-7 w-7" />
            Feature Flags
          </h1>
          <p className="text-muted-foreground">Gestion globale des fonctionnalités par plan et par entreprise</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {([
            { key: "global" as Tab, label: "Flags globaux", icon: Settings },
            { key: "plans" as Tab, label: "Par plan", icon: Layers },
            { key: "company" as Tab, label: "Par entreprise", icon: Building2 },
          ]).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === key
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {tab === "global" && <GlobalFlagsTab />}
        {tab === "plans" && <PlanDefaultsTab />}
        {tab === "company" && <CompanyFeaturesTab />}
      </div>
    </AdminLayout>
  );
}

// ─── Global Flags Tab ─────────────────────────────────────────────────────────

function GlobalFlagsTab() {
  const { data: flags, isLoading, refetch } = trpc.admin.featureFlags.list.useQuery();

  const toggle = trpc.admin.featureFlags.toggle.useMutation({
    onSuccess: () => refetch(),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Flags globaux</h2>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : !flags || flags.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">Aucun feature flag configuré</div>
          ) : (
            <div className="divide-y">
              {flags.map((flag: any) => {
                const overrideCount = Object.keys(flag.overrides ?? {}).length;
                return (
                  <div key={flag.id} className="flex items-center justify-between px-4 py-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">{flag.name}</span>
                        {overrideCount > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {overrideCount} override(s)
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {FLAG_DESCRIPTIONS[flag.name] ?? "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <Badge variant={flag.enabled ? "success" : "secondary"}>
                        {flag.enabled ? "ON" : "OFF"}
                      </Badge>
                      <button
                        type="button"
                        disabled={toggle.isPending}
                        onClick={() => toggle.mutate({ name: flag.name, enabled: !flag.enabled })}
                        className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                        title={flag.enabled ? "Désactiver" : "Activer"}
                      >
                        {flag.enabled ? (
                          <ToggleRight className="h-8 w-8 text-primary" />
                        ) : (
                          <ToggleLeft className="h-8 w-8" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overrides par tenant */}
      {flags && flags.some((f: any) => Object.keys(f.overrides ?? {}).length > 0) && (
        <div>
          <h2 className="text-base font-semibold mb-3">Overrides par tenant</h2>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {flags
                  .filter((f: any) => Object.keys(f.overrides ?? {}).length > 0)
                  .map((flag: any) => (
                    <div key={flag.id} className="px-4 py-4">
                      <div className="font-mono text-sm font-medium mb-2">{flag.name}</div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(flag.overrides as Record<string, boolean>).map(
                          ([companyId, value]) => (
                            <div
                              key={companyId}
                              className="flex items-center gap-1.5 text-xs bg-muted rounded px-2 py-1"
                            >
                              <span className="font-mono text-muted-foreground">{companyId.slice(0, 8)}…</span>
                              <Badge variant={value ? "success" : "destructive"} className="text-xs">
                                {value ? "ON" : "OFF"}
                              </Badge>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── Plan Defaults Tab ────────────────────────────────────────────────────────

function PlanDefaultsTab() {
  const plans = Object.keys(PLAN_DEFAULTS);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold">Fonctionnalités par plan</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Matrice des fonctionnalités incluses par défaut dans chaque plan d'abonnement
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium text-muted-foreground sticky left-0 bg-muted/50 min-w-[180px]">
                    Fonctionnalité
                  </th>
                  {plans.map((plan) => (
                    <th key={plan} className="text-center p-3 font-medium text-muted-foreground min-w-[100px]">
                      <Badge variant="outline">{PLAN_LABELS[plan]}</Badge>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ALL_FEATURES.map((feature) => (
                  <tr key={feature} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="p-3 font-medium sticky left-0 bg-background">
                      <div>{FEATURE_LABELS[feature] ?? feature}</div>
                      <div className="text-xs text-muted-foreground font-mono font-normal">{feature}</div>
                    </td>
                    {plans.map((plan) => {
                      const included = PLAN_DEFAULTS[plan]?.includes(feature);
                      return (
                        <td key={plan} className="text-center p-3">
                          {included ? (
                            <Check className="h-5 w-5 text-success mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-muted-foreground/30 mx-auto" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-4">
        {plans.map((plan) => (
          <Card key={plan}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{PLAN_LABELS[plan]}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{PLAN_DEFAULTS[plan].length}</div>
              <p className="text-xs text-muted-foreground">/ {ALL_FEATURES.length} fonctionnalités</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-xs text-muted-foreground bg-muted rounded-lg p-4">
        <strong>Note :</strong> Ces defaults sont définis côté serveur. Les overrides OPS/Admin par entreprise prennent le dessus sur les defaults du plan.
      </div>
    </div>
  );
}

// ─── Company Features Tab ─────────────────────────────────────────────────────

function CompanyFeaturesTab() {
  const [companySearch, setCompanySearch] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  const { data: companiesData } = trpc.admin.companies.list.useQuery({
    page: 1,
    limit: 10,
    search: companySearch || undefined,
  } as any, {
    enabled: companySearch.length >= 2,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold">Fonctionnalités par entreprise</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Recherchez une entreprise pour voir et modifier ses fonctionnalités actives
        </p>
      </div>

      {/* Company search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher une entreprise..."
          value={companySearch}
          onChange={(e) => { setCompanySearch(e.target.value); setSelectedCompanyId(null); }}
          className="pl-9"
        />
      </div>

      {/* Search results */}
      {companySearch.length >= 2 && !selectedCompanyId && (
        <Card>
          <CardContent className="p-0">
            {(companiesData as any)?.items?.length > 0 ? (
              <div className="divide-y">
                {(companiesData as any).items.map((c: any) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCompanyId(c.id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                  >
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.siret || c.email || "—"}</div>
                    </div>
                    <Badge variant="secondary" className="text-xs">{c.status}</Badge>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-muted-foreground text-sm">Aucune entreprise trouvée</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selected company features */}
      {selectedCompanyId && (
        <CompanyFeatureDetail companyId={selectedCompanyId} onBack={() => setSelectedCompanyId(null)} />
      )}
    </div>
  );
}

function CompanyFeatureDetail({ companyId, onBack }: { companyId: string; onBack: () => void }) {
  const utils = trpc.useUtils();

  const { data: features, isLoading } = trpc.admin.companyFeatures.list.useQuery(
    { companyId } as any,
    { enabled: !!companyId }
  );

  const { data: historyData } = trpc.admin.companyFeatures.history.useQuery(
    { companyId, limit: 20 } as any,
    { enabled: !!companyId }
  );

  const setFeature = trpc.admin.companyFeatures.set.useMutation({
    onSuccess: () => {
      utils.admin.companyFeatures.list.invalidate();
      utils.admin.companyFeatures.history.invalidate();
    },
  });

  const featuresList = (features as any)?.features ?? [];
  const overrideCount = featuresList.filter((f: any) => f.source === "override").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Retour
        </Button>
        <div>
          <h3 className="text-lg font-semibold">{(features as any)?.companyName ?? "..."}</h3>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Plan : <Badge variant="outline" className="text-xs">{(features as any)?.plan ?? "—"}</Badge></span>
            <span>{overrideCount} override(s) actif(s)</span>
          </div>
        </div>
      </div>

      {/* Features grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-4 w-4 text-primary" />
            Fonctionnalités
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <div className="divide-y">
              {featuresList.map((f: any) => (
                <div key={f.featureKey} className="flex items-center justify-between px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{FEATURE_LABELS[f.featureKey] ?? f.featureKey}</span>
                      <Badge
                        variant={f.source === "override" ? "warning" : "secondary"}
                        className="text-xs"
                      >
                        {f.source === "override" ? "Override" : "Plan"}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">{f.featureKey}</div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Badge variant={f.enabled ? "success" : "secondary"}>
                      {f.enabled ? "ON" : "OFF"}
                    </Badge>
                    <button
                      type="button"
                      disabled={setFeature.isPending}
                      onClick={() => setFeature.mutate({
                        companyId,
                        featureKey: f.featureKey,
                        enabled: !f.enabled,
                        reason: "Admin override",
                      } as any)}
                      className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                      title={f.enabled ? "Désactiver" : "Activer"}
                    >
                      {f.enabled ? (
                        <ToggleRight className="h-7 w-7 text-primary" />
                      ) : (
                        <ToggleLeft className="h-7 w-7" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4 text-primary" />
            Historique des changements
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Fonctionnalité</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Action</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Raison</th>
                </tr>
              </thead>
              <tbody>
                {(historyData as any)?.length > 0 ? (
                  (historyData as any).map((log: any) => (
                    <tr key={log.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-muted-foreground">{formatDateTime(log.createdAt)}</td>
                      <td className="p-3">
                        <span className="font-medium">{FEATURE_LABELS[log.featureKey] ?? log.featureKey}</span>
                      </td>
                      <td className="p-3">
                        <Badge variant={log.action === "enabled" ? "success" : "destructive"}>
                          {log.action === "enabled" ? "Activé" : "Désactivé"}
                        </Badge>
                      </td>
                      <td className="p-3 text-muted-foreground text-xs">{log.reason || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="p-6 text-center text-muted-foreground text-sm">
                      Aucun historique
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
